import { tv } from '@heroui/react';
import type React from 'react';
import { memo } from 'react';
import { cn } from '../../cn';

const openClassNames = {
  right: 'translate-x-0',
  left: 'translate-x-0',
  top: 'translate-y-0',
  bottom: 'translate-y-0',
};

const closeClassNames = {
  right: 'translate-x-full',
  left: '-translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

const drawer = tv({
  slots: {
    base: 'fixed inset-0 dark:bg-primary-50/70 bg-black/70 opacity-50 wrapper-all',
    overlay: '',
    wrapper: 'pointer-events-none fixed max-w-full',
    container: 'absolute inset-0 overflow-hidden',
    transition: 'pointer-events-auto relative w-full h-full transform wrapper ease-in-out duration-500',
    body: 'flex flex-col h-full overflow-y-auto shadow-xl',
  },
  variants: {
    position: {
      right: { overlay: 'inset-y-0 right-0' },
      left: { overlay: 'inset-y-0 left-0' },
      top: { overlay: 'inset-x-0 top-0' },
      bottom: { overlay: 'inset-x-0 bottom-0' },
    },
    open: {
      true: {
        base: 'opacity-100 duration-500 ease-in-out visible',
        overlay: 'fixed inset-0 overflow-hidden',
      },
      false: { base: 'opacity-0 duration-500 ease-in-out invisible' },
    },
  },
  // compoundVariants: [
  //   {
  //     position: 'right',
  //     open: false,
  //     class: {
  //       transition: 'translate-x-full',
  //     },
  //   },
  //   {
  //     position: 'top',
  //     open: false,
  //     class: {
  //       transition: '-translate-y-full',
  //     },
  //   },
  //   {
  //     position: 'bottom',
  //     open: false,
  //     class: {
  //       transition: 'translate-y-full',
  //     },
  //   },
  //   {
  //     position: 'left',
  //     open: false,
  //     class: {
  //       transition: '-translate-x-full',
  //     },
  //   },
  //   {
  //     position: ['right', 'left'],
  //     open: true,
  //     class: {
  //       transition: 'translate-x-0',
  //     },
  //   },
  //   {
  //     position: ['top', 'bottom'],
  //     open: true,
  //     class: {
  //       transition: 'translate-y-0',
  //     },
  //   },
  // ],
});

// type DrawerVariants = VariantProps<typeof drawer>;

type DrawerVariants = {
  position?: 'bottom' | 'right' | 'left' | 'top';
  open?: boolean;
};

export interface DrawerProps extends React.PropsWithChildren, DrawerVariants {
  onClose: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const DrawerComponent = ({ open, onClose, position = 'left', children }: DrawerProps) => {
  const { base, overlay, container, wrapper, transition, body } = drawer();
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: <disabled>
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: <disabled>
    <div
      id={`dialog-${position}`}
      className='relative z-10'
      aria-labelledby='slide-over'
      aria-modal='true'
      onClick={onClose}
    >
      <div className={base({ open })} />
      <div className={overlay({ open })}>
        <div className={container()}>
          <div className={wrapper({ position })}>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: <disabled> */}
            <div
              className={cn(
                transition({ open, position }),
                { [closeClassNames[position]]: !open },
                { [openClassNames[position]]: open },
              )}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <div className={body()}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Drawer = memo(DrawerComponent) as unknown as typeof DrawerComponent;
